import type { AiReadingRequest, Gender } from '@entities/saju'
import { sajuApi } from '@entities/saju'
import {
  LandingView,
  Step1GenderName,
  Step2Birthday,
  Step3Time,
  Step4Concerns,
  Step5Confirm,
} from '@features/onboarding'
import { birthInfoSchema } from '@shared/lib/birth-info-schema'
import { capture } from '@shared/lib/posthog'
import { getSavedUserInfo, setSavedUserInfo } from '@shared/lib/saved-user-info'
import { usePollingStore } from '@shared/store/use-polling-store'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute('/saju/$sajuType/')({
  component: Home,
})

type FormStep = 'landing' | 1 | 2 | 3 | 4 | 5

/* ============================================
   Home (root component)
   ============================================ */

function Home() {
  const { sajuType } = Route.useParams()
  const navigate = useNavigate()
  const setPolling = usePollingStore((s) => s.setPolling)

  // --- Submit mutation (POST만 담당, onSuccess → result 라우트로 이동) ---
  const submitMutation = useMutation({
    mutationFn: (body: AiReadingRequest) => sajuApi.aiReadingSubmit(sajuType, body),
    onSuccess: (data, variables) => {
      setPolling(data.hashKey, { sajuType, subjectName: variables.name, sajuName: '정통사주' })
      void navigate({
        to: '/saju/$sajuType/result',
        params: { sajuType },
        search: { hashKey: data.hashKey },
      })
    },
    onError: (err) => {
      toast.error(err.message || '제출에 실패했어요. 다시 시도해주세요.')
    },
  })

  return <FormView mutation={submitMutation} />
}

/* ============================================
   FormView (5-step onboarding + Landing)
   ============================================ */

interface FormViewProps {
  readonly mutation: ReturnType<typeof useMutation<unknown, Error, AiReadingRequest>>
}

function FormView({ mutation }: FormViewProps) {
  const [formStep, setFormStep] = useState<FormStep>(() => {
    capture('view_landing')
    return 'landing'
  })
  const [saveInfo, setSaveInfo] = useState(() => getSavedUserInfo() !== null)

  const changeStep = (step: FormStep) => {
    if (step === 'landing') {
      capture('view_landing')
    } else {
      capture('view_onboarding_step', { step })
    }
    setFormStep(step)
  }

  const saved = getSavedUserInfo()
  const hasSavedInfo =
    saved !== null &&
    saved.name.trim().length > 0 &&
    (saved.gender === 'MALE' || saved.gender === 'FEMALE') &&
    saved.birthYear.length === 4 &&
    saved.birthMonth.length > 0 &&
    saved.birthDay.length > 0

  const form = useForm({
    defaultValues: {
      name: saved?.name ?? '',
      birthYear: saved?.birthYear ?? '',
      birthMonth: saved?.birthMonth ?? '',
      birthDay: saved?.birthDay ?? '',
      birthTime: (saved?.birthTime ?? '') as string,
      gender: (saved?.gender ?? '') as Gender | '',
      concerns: [] as string[],
      concernText: '',
      isLunar: saved?.isLunar ?? false,
    },
    validators: {
      onChange: birthInfoSchema.extend({
        concerns: z.array(z.string()),
        concernText: z.string(),
      }),
    },
    onSubmit: ({ value }) => {
      if (!value.gender) {
        return
      }

      if (saveInfo) {
        setSavedUserInfo({
          name: value.name,
          birthYear: value.birthYear,
          birthMonth: value.birthMonth,
          birthDay: value.birthDay,
          birthTime: value.birthTime,
          gender: value.gender as Gender,
          isLunar: value.isLunar,
        })
      }

      const birthDate = `${value.birthYear}-${value.birthMonth.padStart(2, '0')}-${value.birthDay.padStart(2, '0')}`
      const chipText =
        value.concerns.length > 0 ? `${value.concerns.join(',')} 관련된 운세가 궁금해요` : ''
      const concern = [chipText, value.concernText].filter(Boolean).join('\n') || undefined

      mutation.mutate({
        name: value.name,
        birthDate,
        gender: value.gender as Gender,
        birthTime: value.birthTime || undefined,
        concern,
        targetYear: new Date().getFullYear(),
        lunar: value.isLunar,
      })
    },
  })

  const v = useStore(form.store, (s) => s.values)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
    >
      {formStep === 'landing' && (
        <LandingView
          onStart={() => {
            changeStep(1)
          }}
        />
      )}

      {/* Steps: HeroLayout 내부 애니메이션에 위임, wrapper 애니메이션 없음 */}
      {formStep === 1 && (
        <Step1GenderName
          gender={v.gender}
          onGenderChange={(val) => {
            form.setFieldValue('gender', val as Gender)
          }}
          name={v.name}
          onNameChange={(val) => {
            form.setFieldValue('name', val)
          }}
          onBack={() => {
            changeStep('landing')
          }}
          onNext={() => {
            changeStep(2)
          }}
          hasSavedInfo={hasSavedInfo}
          onSkipToConfirm={() => {
            if (!saved) {
              return
            }
            form.setFieldValue('name', saved.name)
            form.setFieldValue('gender', saved.gender)
            form.setFieldValue('birthYear', saved.birthYear)
            form.setFieldValue('birthMonth', saved.birthMonth)
            form.setFieldValue('birthDay', saved.birthDay)
            form.setFieldValue('birthTime', saved.birthTime)
            form.setFieldValue('isLunar', saved.isLunar)
            changeStep(5)
          }}
        />
      )}
      {formStep === 2 && (
        <Step2Birthday
          name={v.name}
          birthYear={v.birthYear}
          birthMonth={v.birthMonth}
          birthDay={v.birthDay}
          isLunar={v.isLunar}
          onBirthYearChange={(val) => {
            form.setFieldValue('birthYear', val)
          }}
          onBirthMonthChange={(val) => {
            form.setFieldValue('birthMonth', val)
          }}
          onBirthDayChange={(val) => {
            form.setFieldValue('birthDay', val)
          }}
          onIsLunarChange={(val) => {
            form.setFieldValue('isLunar', val)
          }}
          onBack={() => {
            changeStep(1)
          }}
          onNext={() => {
            changeStep(3)
          }}
        />
      )}
      {formStep === 3 && (
        <Step3Time
          birthTime={v.birthTime}
          onBirthTimeChange={(val) => {
            form.setFieldValue('birthTime', val)
          }}
          onBack={() => {
            changeStep(2)
          }}
          onNext={() => {
            changeStep(4)
          }}
        />
      )}
      {formStep === 4 && (
        <Step4Concerns
          name={v.name}
          concerns={v.concerns}
          concernText={v.concernText}
          onConcernsChange={(val) => {
            form.setFieldValue('concerns', val)
          }}
          onConcernTextChange={(val) => {
            form.setFieldValue('concernText', val)
          }}
          onBack={() => {
            changeStep(3)
          }}
          onNext={() => {
            changeStep(5)
          }}
        />
      )}
      {formStep === 5 && (
        <Step5Confirm
          formValues={v}
          onBack={() => {
            changeStep(4)
          }}
          onEdit={changeStep}
          isPending={mutation.isPending}
          saveInfo={saveInfo}
          onSaveInfoChange={setSaveInfo}
        />
      )}
    </form>
  )
}
